import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageSquare, Clock, Trash2, Search, X, ChevronRight,
  Calendar, Share2, Bot, User, Zap, TrendingUp, Hash, RefreshCw,
  MoreVertical, Bookmark, Star, Filter
} from 'lucide-react';
import ShareModal from './ShareModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ChatHistory = ({ user, onSelectConversation, onClose }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [shareModal, setShareModal] = useState({ open: false, conversation: null });
  const [filter, setFilter] = useState('all');
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => { loadHistory(); }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/chat/history/${user.id}?limit=100`);
      setConversations(res.data);
    } catch (e) {
      console.error('Error loading history:', e);
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar esta conversación?')) return;
    try {
      await axios.delete(`${API}/chat/conversation/${conversationId}`);
      toast.success('Conversación eliminada');
      setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const getDateGroup = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return 'Esta semana';
    if (diffDays < 30) return 'Este mes';
    return 'Antes';
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return date.toLocaleDateString('es-MX', { weekday: 'short' });
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  const getPreview = (conversation) => {
    if (!conversation.messages?.length) return 'Sin mensajes';
    const last = [...conversation.messages].reverse().find(m => m.role === 'user');
    const text = last?.content || 'Sin mensajes';
    return text.length > 80 ? text.substring(0, 80) + '...' : text;
  };

  const getLastBotMessage = (conversation) => {
    if (!conversation.messages?.length) return null;
    const last = [...conversation.messages].reverse().find(m => m.role === 'assistant');
    return last?.content?.substring(0, 60) || null;
  };

  const getAgentColor = (agentType) => {
    const map = {
      user: 'from-red-500 to-rose-600',
      business: 'from-orange-500 to-amber-600',
      admin: 'from-purple-500 to-violet-600',
      general: 'from-blue-500 to-indigo-600',
    };
    return map[agentType?.toLowerCase()] || 'from-red-500 to-rose-600';
  };

  const getAgentLabel = (agentType) => {
    const map = {
      user: 'Personal',
      business: 'Negocios',
      admin: 'Admin',
      general: 'General',
    };
    return map[agentType?.toLowerCase()] || agentType || 'Chat';
  };

  const filteredConversations = useMemo(() => {
    let list = conversations;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(c => getPreview(c).toLowerCase().includes(q) || (c.agent_type || '').toLowerCase().includes(q));
    }
    if (filter !== 'all') {
      list = list.filter(c => (c.agent_type || '').toLowerCase() === filter);
    }
    return list;
  }, [conversations, searchQuery, filter]);

  const groupedConversations = useMemo(() => {
    const groups = {};
    const order = ['Hoy', 'Ayer', 'Esta semana', 'Este mes', 'Antes'];
    filteredConversations.forEach(conv => {
      const group = getDateGroup(conv.updated_at || conv.created_at);
      if (!groups[group]) groups[group] = [];
      groups[group].push(conv);
    });
    return order.filter(g => groups[g]).map(g => ({ label: g, items: groups[g] }));
  }, [filteredConversations]);

  const stats = useMemo(() => {
    const today = conversations.filter(c => getDateGroup(c.updated_at || c.created_at) === 'Hoy').length;
    const week = conversations.filter(c => ['Hoy', 'Ayer', 'Esta semana'].includes(getDateGroup(c.updated_at || c.created_at))).length;
    const totalMessages = conversations.reduce((acc, c) => acc + (c.messages?.length || 0), 0);
    return { total: conversations.length, today, week, totalMessages };
  }, [conversations]);

  return (
    <div className="h-full flex flex-col bg-transparent">
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <Clock size={15} className="text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm leading-tight">Historial de Chats</h3>
              <p className="text-gray-500 text-[10px]">{stats.total} conversaciones guardadas</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={loadHistory} className="p-1.5 hover:bg-white/10 rounded-lg" title="Actualizar">
              <RefreshCw size={14} className="text-gray-500" />
            </button>
            {onClose && (
              <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg">
                <X size={16} className="text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[
            { label: 'Hoy', value: stats.today, icon: <Zap size={11} />, color: 'text-yellow-400' },
            { label: 'Esta semana', value: stats.week, icon: <TrendingUp size={11} />, color: 'text-green-400' },
            { label: 'Mensajes', value: stats.totalMessages, icon: <Hash size={11} />, color: 'text-blue-400' },
          ].map(s => (
            <div key={s.label} className="bg-white/5 rounded-xl p-2 text-center">
              <div className={`flex items-center justify-center gap-1 mb-0.5 ${s.color}`}>{s.icon}<span className="text-[9px] font-medium">{s.label}</span></div>
              <p className="text-white font-bold text-base leading-none">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar conversaciones..."
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-white text-xs placeholder-gray-600 outline-none focus:border-red-500/50 transition-colors"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X size={12} className="text-gray-500 hover:text-white" />
            </button>
          )}
        </div>

        {/* Filter Pills */}
        <div className="flex gap-1 overflow-x-auto no-scrollbar">
          {[{ id: 'all', label: 'Todos' }, { id: 'user', label: 'Personal' }, { id: 'business', label: 'Negocios' }].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap transition-all ${
                filter === f.id
                  ? 'bg-red-500 text-white shadow-sm'
                  : 'bg-white/5 text-gray-500 hover:bg-white/10 hover:text-gray-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {loading ? (
          <div className="space-y-2 pt-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="rounded-2xl bg-white/5 p-3 animate-pulse">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-xl bg-white/10" />
                  <div className="flex-1">
                    <div className="h-2.5 bg-white/10 rounded-full w-20 mb-1" />
                    <div className="h-2 bg-white/5 rounded-full w-12" />
                  </div>
                </div>
                <div className="h-2.5 bg-white/10 rounded-full w-full mb-1.5" />
                <div className="h-2 bg-white/5 rounded-full w-3/4" />
              </div>
            ))}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-3">
              <MessageSquare className="text-gray-600" size={28} />
            </div>
            <p className="text-gray-400 text-sm font-medium">
              {searchQuery ? 'Sin resultados' : 'Sin historial'}
            </p>
            <p className="text-gray-600 text-xs mt-1">
              {searchQuery ? 'Intenta con otras palabras' : 'Tus conversaciones aparecerán aquí'}
            </p>
          </div>
        ) : (
          groupedConversations.map(group => (
            <div key={group.label}>
              {/* Group label */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-600">{group.label}</span>
                <div className="flex-1 h-px bg-white/5" />
                <span className="text-[10px] text-gray-700 bg-white/5 px-1.5 py-0.5 rounded-full">{group.items.length}</span>
              </div>

              {/* Conversation cards */}
              <div className="space-y-1.5">
                {group.items.map((conv) => {
                  const preview = getPreview(conv);
                  const botReply = getLastBotMessage(conv);
                  const msgCount = conv.messages?.length || 0;
                  const agentColor = getAgentColor(conv.agent_type);
                  const isHovered = hoveredId === conv.conversation_id;

                  return (
                    <div
                      key={conv.conversation_id}
                      onClick={() => onSelectConversation(conv)}
                      onMouseEnter={() => setHoveredId(conv.conversation_id)}
                      onMouseLeave={() => setHoveredId(null)}
                      className="group relative rounded-2xl border cursor-pointer transition-all duration-200 overflow-hidden"
                      style={{
                        background: isHovered ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)',
                        borderColor: isHovered ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.07)',
                        transform: isHovered ? 'translateX(2px)' : 'none',
                      }}
                    >
                      {/* Red left accent */}
                      <div className={`absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b ${agentColor} opacity-${isHovered ? '100' : '40'} transition-opacity`} />

                      <div className="p-3 pl-4">
                        {/* Top row */}
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-2 min-w-0">
                            {/* Agent avatar */}
                            <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${agentColor} flex items-center justify-center flex-shrink-0`}>
                              <Bot size={11} className="text-white" />
                            </div>
                            <span className="text-white text-xs font-semibold truncate capitalize">
                              {getAgentLabel(conv.agent_type)}
                            </span>
                            <span className="text-gray-700 text-[10px] bg-white/5 px-1.5 py-0.5 rounded-full flex-shrink-0">
                              {msgCount} msg
                            </span>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-gray-600 text-[10px]">{formatTime(conv.updated_at || conv.created_at)}</span>
                            {/* Actions (visible on hover) */}
                            <div className={`flex items-center gap-0.5 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                              <button
                                onClick={(e) => { e.stopPropagation(); setShareModal({ open: true, conversation: conv }); }}
                                className="p-1 text-gray-500 hover:text-blue-400 hover:bg-blue-500/20 rounded-lg"
                              >
                                <Share2 size={11} />
                              </button>
                              <button
                                onClick={(e) => deleteConversation(conv.conversation_id, e)}
                                className="p-1 text-gray-500 hover:text-red-400 hover:bg-red-500/20 rounded-lg"
                              >
                                <Trash2 size={11} />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* User message preview */}
                        <p className="text-gray-300 text-xs leading-relaxed line-clamp-2 mb-1">
                          <span className="text-gray-600 mr-1">›</span>{preview}
                        </p>

                        {/* Bot reply snippet */}
                        {botReply && (
                          <div className="flex items-start gap-1.5 mt-1.5">
                            <div className="w-3.5 h-3.5 rounded-md bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Bot size={8} className="text-red-400" />
                            </div>
                            <p className="text-gray-600 text-[10px] leading-relaxed line-clamp-1 italic">
                              {botReply}...
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModal.open}
        onClose={() => setShareModal({ open: false, conversation: null })}
        itemType="conversation"
        itemId={shareModal.conversation?.conversation_id}
        itemTitle={`Chat: ${getPreview(shareModal.conversation || {})}`}
        itemData={{ userId: user.id }}
      />
    </div>
  );
};

export default ChatHistory;
